// src/components/students/StudentList.jsx
import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Plus, 
  Eye, 
  Edit3, 
  Copy, 
  Trash2, 
  Search,
  Filter,
  Mail,
  Phone,
  User,
  X,
  AlertCircle
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useStudents } from './StudentProvider';

// Individual Student List Item Component
const StudentListItem = ({ student, onView, onEdit, onClone, onDelete, isSelected, onToggleSelect }) => {

  const firstName = student.studentFirstName || student.student_first_name || student.firstName || '';
  const lastName = student.studentLastName || student.student_last_name || student.lastName || '';
  const age = student.studentAge || student.student_age || student.age || 0;
  const gender = student.gender || '';
  const experience = student.experience || '';
  const parentName = student.parentName || student.parent_name || '';
  const parentEmail = student.parentEmail || student.parent_email || '';
  const parentPhone = student.parentPhone || student.parent_phone || '';

  const getGenderPlaceholderStyle = () => {
    switch (gender?.toLowerCase()) {
      case 'female':
        return 'bg-gradient-to-br from-pink-300 to-pink-500';
      case 'male':
        return 'bg-gradient-to-br from-blue-300 to-blue-500';
      default:
        return 'bg-gradient-to-br from-gray-300 to-gray-500';
    }
  };

  const getExperienceColor = (exp) => {
    switch (exp?.toLowerCase()) {
      case 'beginner': return 'bg-[#B7F1B6] text-[#3C3C3C]';
      case 'intermediate': return 'bg-[#FFECC5] text-[#3C3C3C]';
      case 'advanced': return 'bg-[#FFC6C5] text-[#3C3C3C]';
      default: return 'bg-[#D9D9D9] text-[#3C3C3C]';
    }
  };

  return (
    <div
      className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer ${
        isSelected ? 'ring-2 ring-[#7A99D8] bg-blue-50 dark:bg-blue-900/20' : ''
      }`}
    >
      <div className="flex items-center p-4 gap-4">
        {/* Checkbox */}
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onToggleSelect(student.id);
            }}
            className="w-4 h-4 text-[#7A99D8] border-gray-300 rounded focus:ring-[#7A99D8] cursor-pointer"
          />
        </div>

        {/* Student Photo */}
        <div className="relative w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden">
          {student.photoUrl || student.photo_url ? (
            <img
              src={student.photoUrl || student.photo_url}
              alt={`${firstName} ${lastName}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className={`w-full h-full flex items-center justify-center text-white text-lg font-medium ${getGenderPlaceholderStyle()}`}
            style={{ display: (student.photoUrl || student.photo_url) ? 'none' : 'flex' }}
          >
            {firstName.charAt(0)}{lastName.charAt(0)}
          </div>
        </div>

        {/* Student Name & Info */}
        <div className="flex-1 min-w-0" onClick={() => onView(student)}>
          <div className="mb-1">
            <h3 className="text-sm font-medium text-[#3C3C3C] dark:text-white truncate">
              {firstName} {lastName}
            </h3>
          </div>
          <div className="flex items-center text-xs text-gray-600 dark:text-gray-300 mb-1">
            <User className="w-3 h-3 mr-1 flex-shrink-0" />
            <span className="truncate">Age {age}</span>
          </div>
          {experience && (
            <div>
              <span className={`text-xs px-2 py-1 rounded-full ${getExperienceColor(experience)}`}>
                {experience}
              </span>
            </div>
          )}
        </div>

        {/* Parent Name */}
        <div className="hidden md:flex items-center text-xs text-gray-600 dark:text-gray-300 min-w-0 w-32">
          <User className="w-3 h-3 mr-1 flex-shrink-0" />
          <span className="truncate">{parentName}</span>
        </div>

        {/* Parent Email */}
        <div className="hidden lg:flex items-center text-xs text-gray-600 dark:text-gray-300 min-w-0 w-40">
          <Mail className="w-3 h-3 mr-1 flex-shrink-0" />
          <span className="truncate">{parentEmail}</span>
        </div>

        {/* Parent Phone */}
        <div className="hidden sm:flex items-center text-xs text-gray-600 dark:text-gray-300 min-w-0 w-32">
          <Phone className="w-3 h-3 mr-1 flex-shrink-0" />
          <span className="truncate">{parentPhone}</span>
        </div>

        {/* Status */}
        <div className="flex items-center w-16">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            student.status === 'active' 
              ? 'bg-emerald-100 text-emerald-700' 
              : 'bg-gray-100 text-gray-700'
          }`}>
            {student.status === 'active' ? 'Active' : 'Inactive'}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-1 w-32">
          <button
            onClick={(e) => { e.stopPropagation(); onView(student); }}
            className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 p-1.5 rounded transition-colors"
            title="View Details"
          >
            <Eye className="w-3 h-3 text-[#3C3C3C] dark:text-white" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(student); }}
            className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 p-1.5 rounded transition-colors"
            title="Edit Student"
          >
            <Edit3 className="w-3 h-3 text-[#3C3C3C] dark:text-white" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onClone(student); }}
            className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 p-1.5 rounded transition-colors"
            title="Clone Student"
          >
            <Copy className="w-3 h-3 text-[#3C3C3C] dark:text-white" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(student); }}
            className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 p-1.5 rounded transition-colors"
            title="Delete Student"
          >
            <Trash2 className="w-3 h-3 text-red-600" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Main StudentList Component
const StudentList = () => {
  const navigate = useNavigate();
  const { 
    students, 
    loading, 
    error, 
    fetchStudents, 
    deleteStudent,
    cloneStudent,
    clearError
  } = useStudents();

  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('studentList-sortBy') || 'name';
  });
  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('studentList-sortOrder') || 'asc';
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [filters, setFilters] = useState({
    experience: '',
    status: 'active'
  });

  // Load students on mount
  useEffect(() => {
    fetchStudents();
  }, []);

  // Persist sort preferences
  useEffect(() => {
    localStorage.setItem('studentList-sortBy', sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem('studentList-sortOrder', sortOrder);
  }, [sortOrder]);

  // Filter students based on search and filters
  const filteredStudents = students.filter(student => {
    const firstName = student.studentFirstName || student.student_first_name || student.firstName || '';
    const lastName = student.studentLastName || student.student_last_name || student.lastName || '';
    const parentEmail = student.parentEmail || student.parent_email || '';
    
    const matchesSearch = !searchTerm || 
      firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      parentEmail.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesExperience = !filters.experience || student.experience === filters.experience;
    const matchesStatus = !filters.status || student.status === filters.status;

    return matchesSearch && matchesExperience && matchesStatus;
  });

  // Sort students
  const sortedStudents = [...filteredStudents].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case 'name':
        const aFirstName = a.studentFirstName || a.student_first_name || a.firstName || '';
        const aLastName = a.studentLastName || a.student_last_name || a.lastName || '';
        const bFirstName = b.studentFirstName || b.student_first_name || b.firstName || '';
        const bLastName = b.studentLastName || b.student_last_name || b.lastName || '';
        aValue = `${aFirstName} ${aLastName}`.toLowerCase();
        bValue = `${bFirstName} ${bLastName}`.toLowerCase();
        break;
      case 'age':
        aValue = a.studentAge || a.student_age || a.age || 0;
        bValue = b.studentAge || b.student_age || b.age || 0;
        break;
      case 'experience':
        const expOrder = { 'Beginner': 1, 'Intermediate': 2, 'Advanced': 3 };
        aValue = expOrder[a.experience] || 0;
        bValue = expOrder[b.experience] || 0;
        break;
      case 'created':
        aValue = new Date(a.created_at || a.createdAt || 0);
        bValue = new Date(b.created_at || b.createdAt || 0);
        break;
      default:
        const defaultFirstA = a.studentFirstName || a.student_first_name || a.firstName || '';
        const defaultLastA = a.studentLastName || a.student_last_name || a.lastName || '';
        const defaultFirstB = b.studentFirstName || b.student_first_name || b.firstName || '';
        const defaultLastB = b.studentLastName || b.student_last_name || b.lastName || '';
        aValue = `${defaultFirstA} ${defaultLastA}`.toLowerCase();
        bValue = `${defaultFirstB} ${defaultLastB}`.toLowerCase();
    }

    if (sortOrder === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  // Get unique values for filter dropdowns
  const getUniqueValues = (field) => {
    const values = students.map(student => student[field]).filter(Boolean);
    return [...new Set(values)].sort();
  };

  // Action handlers
  const handleView = (student) => {
    navigate(`/students/view/${student.id}`);
  };

  const handleEdit = (student) => {
    navigate(`/students/edit/${student.id}`);
  };

  const handleClone = async (student) => {
    try {
      await cloneStudent(student.id);
      console.log(`Successfully cloned student: ${student.studentFirstName || student.firstName} ${student.studentLastName || student.lastName}`);
    } catch (error) {
      console.error('Failed to clone student:', error);
    }
  };

  const handleDelete = (student) => {
    setStudentToDelete(student);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (studentToDelete) {
      try {
        await deleteStudent(studentToDelete.id);
      } catch (error) {
        console.error('Failed to delete student:', error);
      }
    }
    setShowDeleteModal(false);
    setStudentToDelete(null);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSortBy('name');
    setSortOrder('asc');
    setFilters({
      experience: '',
      status: 'active'
    });
    localStorage.removeItem('studentList-sortBy');
    localStorage.removeItem('studentList-sortOrder');
  };

  // Selection handlers
  const handleToggleSelect = (studentId) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === sortedStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(sortedStudents.map(s => s.id));
    }
  };

  const handleBulkDelete = () => {
    if (selectedStudents.length > 0) {
      setShowBulkDeleteModal(true);
    }
  };

  const confirmBulkDelete = async () => {
    try {
      // Delete each selected student
      for (const studentId of selectedStudents) {
        await deleteStudent(studentId);
      }
      setSelectedStudents([]);
    } catch (error) {
      console.error('Failed to delete students:', error);
    }
    setShowBulkDeleteModal(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Users className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-white">Students</h1>
              <p className="text-sm text-gray-600 dark:text-gray-300">{sortedStudents.length} students • Manage student profiles and enrollments</p>
            </div>
          </div>
          <Link
            to="/students/add"
            className="bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C] px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Student</span>
          </Link>
        </div>
      </div>

      <div className="px-6 py-6">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <X className="w-4 h-4 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Error loading students</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
            <button
              onClick={clearError}
              className="text-red-600 hover:text-red-800 p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search students by name or parent email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
              />
            </div>

            {/* Filter Toggle */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 text-sm text-[#3C3C3C] dark:text-white hover:text-[#7A99D8] transition-colors"
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
              </button>

              <button
                onClick={clearFilters}
                className="text-sm text-gray-600 dark:text-gray-300 hover:text-[#3C3C3C] dark:hover:text-gray-100 transition-colors"
              >
                Clear all filters
              </button>
            </div>
          </div>

          {/* Expandable Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-white mb-2">Experience Level</label>
                <select
                  value={filters.experience}
                  onChange={(e) => setFilters(prev => ({ ...prev, experience: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                >
                  <option value="">All Experience Levels</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-white mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Results Summary, Selection Actions, and Sorting */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <span className="text-gray-600 dark:text-gray-300">
              Showing {sortedStudents.length} of {students.length} students
            </span>
            {selectedStudents.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-[#7A99D8]">
                  {selectedStudents.length} selected
                </span>
                <button
                  onClick={handleBulkDelete}
                  className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Selected
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-[#3C3C3C] dark:text-white">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent text-sm"
              >
                <option value="name">Name</option>
                <option value="age">Age</option>
                <option value="experience">Experience</option>
                <option value="created">Date Added</option>
              </select>
            </div>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="flex items-center gap-1 text-sm text-[#3C3C3C] dark:text-white hover:text-[#7A99D8] transition-colors"
            >
              {sortOrder === 'asc' ? '↑' : '↓'} {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
              <span className="text-gray-600 dark:text-gray-300">Loading students...</span>
            </div>
          </div>
        )}

        {/* Students List */}
        {!loading && sortedStudents.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Table Header */}
            <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
              <div className="flex items-center gap-4 text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={sortedStudents.length > 0 && selectedStudents.length === sortedStudents.length}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-[#7A99D8] border-gray-300 rounded focus:ring-[#7A99D8] cursor-pointer"
                  />
                </div>
                <div className="flex-1">Student & Details</div>
                <div className="hidden md:block w-32">Parent Name</div>
                <div className="hidden lg:block w-40">Parent Email</div>
                <div className="hidden sm:block w-32">Parent Phone</div>
                <div className="w-16">Status</div>
                <div className="w-32">Actions</div>
              </div>
            </div>

            {/* Students List */}
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {sortedStudents.map(student => (
                <StudentListItem
                  key={student.id}
                  student={student}
                  onView={handleView}
                  onEdit={handleEdit}
                  onClone={handleClone}
                  onDelete={handleDelete}
                  isSelected={selectedStudents.includes(student.id)}
                  onToggleSelect={handleToggleSelect}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && sortedStudents.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <Users className="w-12 h-12 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-xl font-semibold text-[#3C3C3C] dark:text-white mb-3">
              {students.length === 0 ? 'No students yet' : 'No students found'}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-md mx-auto">
              {students.length === 0 
                ? "Get started by adding your first student to track enrollments and progress."
                : "Try adjusting your search or filters to find students."
              }
            </p>
            <Link
              to="/students/add"
              className="bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C] px-6 py-3 rounded-lg inline-flex items-center gap-2 transition-colors font-medium"
            >
              <Plus className="w-5 h-5" />
              {students.length === 0 ? 'Add Your First Student' : 'Add New Student'}
            </Link>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && studentToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <AlertCircle className="w-6 h-6 text-red-500 mr-3" />
                  <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-white">Delete Student</h3>
                </div>

                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Are you sure you want to delete "{(studentToDelete.studentFirstName || studentToDelete.firstName || '')} {(studentToDelete.studentLastName || studentToDelete.lastName || '')}"? This action cannot be undone.
                </p>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setStudentToDelete(null);
                    }}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Delete Student
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Delete Confirmation Modal */}
        {showBulkDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <AlertCircle className="w-6 h-6 text-red-500 mr-3" />
                  <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-white">Delete Multiple Students</h3>
                </div>

                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Are you sure you want to delete {selectedStudents.length} student{selectedStudents.length !== 1 ? 's' : ''}? This action cannot be undone.
                </p>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowBulkDeleteModal(false)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmBulkDelete}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Delete {selectedStudents.length} Student{selectedStudents.length !== 1 ? 's' : ''}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentList;